// src/services/guestLinks.js — the OWNER's side of a guest link.
//
// Minting, listing and revoking links to people who are not on Allo. The link
// itself is created by the server (server/functions/guestLinks.js), because the
// two things that make it safe cannot be done here:
//
//   🟥 THE PASSCODE IS HASHED SERVER-SIDE. A passcode checked in the browser is
//      a suggestion — anyone can open the console and skip it.
//   🟥 AND THE OWNER IS PROVEN BY AN ID TOKEN, not by a uid in a request body,
//      which anyone could type.
//
// Reading is a different matter: `guestLinks` rows belong to the owner, so the
// list is a plain Firestore query with a live listener. The guest can never read
// that collection at all (see firestore.rules) — what they see comes from the
// server's /peek, which returns a deliberately thin shape.

import {
  collection, query, where, onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { BACKEND_URL } from './socket';

/** The four the picker offers. 15 days is the ceiling, by product decision. */
export const LINK_DAYS = [1, 3, 7, 15];

async function post(path, body) {
  const user = auth.currentUser;
  const idToken = user ? await user.getIdToken() : null;
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, idToken }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    const err = new Error(json.error || `http_${res.status}`);
    err.code = json.error || `http_${res.status}`;
    throw err;
  }
  return json;
}

/**
 * Mint a link for ONE person.
 * @param days      1 | 3 | 7 | 15
 * @param passcode  optional; when set the guest must type it to get in
 * @param note      optional label so a list of links is not a list of ids
 */
export function createGuestLink({ days = 1, passcode = '', note = '', kind = 'chat' } = {}) {
  return post('/api/guest/create', {
    /* ⭐ 'chat' (a temporary conversation) or 'call' (one phone call, no thread).
       🟥 The DEFAULT IS 'chat' on both sides. Every link written before call
       links existed has no `kind` field, and they are all chat links — a default
       of 'call' would reclassify the entire history on the day this ships. */
    kind: kind === 'call' ? 'call' : 'chat',
    days: LINK_DAYS.includes(Number(days)) ? Number(days) : 1,
    passcode: passcode ? String(passcode) : '',
    note: note || '',
    /* 🟢 MINT A LINK THAT POINTS BACK AT THIS EXACT BROWSER TAB. On localhost
       that means the link you just made actually opens against `npm start`
       instead of a production domain you are not running; in production it is
       the production domain, because that is where the tab already is.

       This is a REQUEST, not an instruction — the server only honours it if the
       origin is on its published allowlist (ALLO_WEB_ORIGINS), because a URL the
       client dictates is a URL our away-email would deliver on someone else's
       behalf. An unknown origin silently falls back to the default. */
    origin: (typeof window !== 'undefined' && window.location && window.location.origin) || '',
  });
}

/**
 * 🔧 REPAIR MY ACCOUNT — undo a profile overwritten by a bad self-join.
 *
 * Opening your own guest link in a browser where you were already signed in used
 * to run the join AS YOU, which overwrote your name, username and photo with the
 * form's values and stamped `isGuest` on your account. The server can put it
 * back: your name and photo are still mirrored in every chat you are in and on
 * every post you wrote, so it reads them from there. Safe to run when nothing is
 * wrong — it reports `clearedFlags: []` and changes nothing.
 */
export function repairMyAccount() {
  return post('/api/guest/repair-account', {});
}

/** End it now: the link stops working and the conversation is deleted. */
export function revokeGuestLink(token) {
  return post('/api/guest/revoke', { token: String(token) });
}

/**
 * Remove the row from my history.
 *
 * 🟥 NOT THE SAME AS REVOKE, and the two are separate calls for a reason.
 *    revokeGuestLink ENDS a live conversation and deliberately keeps the record.
 *    This removes the RECORD. A single route doing both would be a button
 *    labelled "delete" that also hangs up on somebody.
 *
 * 🟢 The server revokes first if the link is still live, so deleting a row can
 *    never leave a working token pointing at nothing.
 */
export function deleteGuestLink(token) {
  return post('/api/guest/delete', { token: String(token) });
}

/** 'chat' | 'call' — a link written before call links existed is a chat link. */
export function linkKind(link) {
  return (link && link.kind) === 'call' ? 'call' : 'chat';
}

/**
 * What happened on a call link, in one word.
 *
 * 🟢 'used' IS NOT 'expired'. A link spent by a successful call did its job; one
 *    that ran out of time never got used. A history that shows both as "ended"
 *    cannot answer "did they ever actually reach me".
 */
export function callLinkState(link, now = Date.now()) {
  if (!link) return 'expired';
  if (link.status === 'revoked') return 'revoked';
  if (link.status === 'used') return 'used';
  if (Number(link.expiresAt) <= now) return 'expired';
  return link.guest ? 'ready' : 'waiting';
}

/**
 * What has actually been attempted on a call link.
 *
 * 🟢 EVERY ATTEMPT IS RECORDED, NOT JUST THE ONE THAT SPENT IT — see
 *    noteGuestCallEnded in server/functions/guestLinks.js. A link that reads
 *    "expired" after four unanswered rings and one that expired untouched are
 *    completely different events, and the only place the difference survives is
 *    this list.
 *
 * 🟥 THE STATE IS NOT DERIVED FROM THE ATTEMPTS. `answered > 0` looks like the
 *    same question as "was this link spent", and it is not: an attempt row is
 *    written by the history broadcast while `status: 'used'` is written by the
 *    same call one step later, so during that window a link can have an answered
 *    attempt and still be live. callLinkState() reads the status field, which is
 *    the fact; this only ever describes what was tried.
 */
export function callAttemptSummary(link) {
  const list = Array.isArray(link && link.callAttempts) ? link.callAttempts : [];
  const answered = list.filter((a) => a && a.outcome === 'answered').length;
  const last = list.length ? list[list.length - 1] : null;
  return {
    total: list.length,
    answered,
    missed: list.length - answered,
    lastAt: (last && Number(last.at)) || Number(link && link.lastCallAt) || 0,
    lastOutcome: (last && last.outcome) || (link && link.lastCallOutcome) || '',
    /* the seconds actually spoken, across every attempt */
    talkedSec: list.reduce((n, a) => n + (Number(a && a.durationSec) || 0), 0),
  };
}

/**
 * Every link I have ever made, newest first — live.
 *
 * 🟢 EXPIRED ONES STAY IN THE LIST ON PURPOSE. "Keep track of the non-Allo
 *    people I have talked to" is the part of this feature that outlives the
 *    conversation, so a link whose chat has been deleted is still a row: who it
 *    was, when, and that it has ended.
 *
 * @param kind 'chat' | 'call' | 'all' — which links this caller wants.
 *
 * 🟥 THE FILTER LIVES HERE, AND EVERY CALLER STATES WHAT IT WANTS. Chat links
 *    and call links share one collection because they are one row type with one
 *    expiry sweep and one owner rule. They do NOT share a surface: a call link
 *    has no conversation and a chat link is not spent by a phone call, so a
 *    chat list showing "Waiting for someone to join" over a call link is telling
 *    somebody to wait for a thread that will never exist. The default is 'all'
 *    rather than 'chat' on purpose — a default that quietly filtered would make
 *    every existing caller change behaviour without its code changing.
 */
export function watchMyGuestLinks(myUid, cb, kind = 'all') {
  if (!myUid) { cb([]); return () => {}; }
  /* 🟥 NO orderBy IN THE QUERY, AND THAT IS DELIBERATE. `where('ownerUid','==')`
     combined with `orderBy('createdAt')` is a COMPOSITE query, and Firestore
     refuses those until someone creates the matching index by hand. On a brand
     new collection nobody has, so the listen fails with `failed-precondition`
     every single time — and a failed listen is not a quiet no-op: the SDK tears
     the target down, then the watch stream delivers a response for the target
     that no longer exists, and the whole app dies with
     "INTERNAL ASSERTION FAILED: Unexpected state (ID: ca9)".

     A single equality filter needs no index at all. One owner has a handful of
     links, so sorting them here costs nothing and removes both the crash and a
     console step from the setup. */
  const q = query(collection(db, 'guestLinks'), where('ownerUid', '==', myUid));
  return onSnapshot(q, (snap) => {
    const now = Date.now();
    const rows = snap.docs.map((d) => {
      const l = { id: d.id, ...d.data() };
      /* the server flips `status` on its sweep; until that runs, say the truth.
         🟥 A SPENT CALL LINK IS NOT LIVE EITHER. `status: 'used'` is written the
         moment a call is answered, hours before the expiry it still carries —
         without it here a link that has done its one job would sit in the
         "Active" half of the list with a countdown, offering a Copy button for a
         URL that now refuses everyone. */
      const expired = l.status === 'expired' || l.status === 'revoked'
        || l.status === 'used' || (l.expiresAt || 0) <= now;
      return { ...l, expired, live: !expired };
    })
      .filter((l) => kind === 'all' || linkKind(l) === kind);
    rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));   // newest first
    cb(rows);
  }, (e) => {
    /* 🟢 SAY SO. This used to hand back an empty array, which renders as
       "No links yet" — indistinguishable from the truth, and the reason a
       permission or index failure could hide here indefinitely. */
    console.error('[guestLinks] listener failed:', e && (e.code || e.message), e);
    cb([]);
  });
}

/**
 * The link row behind a given guest CHAT, live.
 *
 * 🟢 QUERIED BY chatId, NOT BY TOKEN, because the surfaces that want it — the
 *    card at the top of the thread, chat info — have a chat and no token. The
 *    rules restrict `guestLinks` to `ownerUid == me`, so this simply returns
 *    nothing for anyone else, including the guest: they have no business reading
 *    the record that describes their own link.
 *
 * 🟥 SINGLE EQUALITY FILTER, then match in code. Same reason as watchMyGuestLinks
 *    — anything Firestore cannot serve from an automatic index needs one built by
 *    hand, and an index nobody built is a listener that fails forever.
 */
export function watchGuestLinkForChat(chatId, myUid, cb) {
  if (!chatId || !myUid) { cb(null); return () => {}; }
  const q = query(collection(db, 'guestLinks'), where('ownerUid', '==', myUid));
  return onSnapshot(q, (snap) => {
    const row = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .find((l) => l.chatId === chatId) || null;
    cb(row ? { ...row, hasPasscode: !!(row.passcodeHash || row.passcodePlain) } : null);
  }, (e) => {
    console.warn('[guestLinks] link-for-chat listener failed:', e && (e.code || e.message));
    cb(null);
  });
}

/** "3 days left", "4 hours left", "expired" — one phrasing, used everywhere. */
export function timeLeftLabel(expiresAt) {
  const ms = (expiresAt || 0) - Date.now();
  if (ms <= 0) return 'expired';
  const d = Math.floor(ms / 86400000);
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'} left`;
  const h = Math.floor(ms / 3600000);
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'} left`;
  const m = Math.max(1, Math.floor(ms / 60000));
  return `${m} min left`;
}

/* ── the flags every other surface asks about ──────────────────────────────
   A guest chat is an ordinary chat doc with `isGuest` on it, which is what lets
   the chat list, previews, ordering and badges work with no changes. These are
   the three questions the rest of the app needs answered about one. */

/* 🟢 `isGuest` ON THE CHAT DOC IS THE ONLY SIGNAL, and there is deliberately no
   id-shaped tell to fall back on: a guest chat carries the ordinary 1-on-1 chat
   id (`[a,b].sort().join('_')`) precisely so that every path which already works
   for a 1-on-1 works for it — see the long note in server/functions/guestLinks.js.
   An earlier draft used a `guest_` prefix and a startsWith() check here; that
   prefix is gone, and a check for it would now silently answer "no" forever. */
export const isGuestChat = (chat) => !!(chat && chat.isGuest);

export const guestChatExpired = (chat) =>
  isGuestChat(chat) && (!chat.expiresAt || chat.expiresAt <= Date.now());

/** The guest's uid in this chat, for a surface that has the chat but not the link. */
export const guestUidOf = (chat) => (isGuestChat(chat) ? (chat.guestUid || null) : null);

/* 🟥 A GUEST IS NOT A CONTACT. `/api/guest/join` writes the guest's user doc with
   `isGuest: true` (server/functions/guestLinks.js), which is deliberately the only
   thing that distinguishes it from any other account — everything else about the
   shape is identical so sealing, avatars and presence need no special case.

   That sameness is exactly why every people-picker has to ask this question: a
   directory search returns the guest like anybody else, and dropping them into a
   group would outlive the link, survive its expiry, and hand a stranger a room
   full of people who never agreed to talk to them. So the pickers filter on this,
   and the rules deny the write besides. */
export const isGuestUser = (u) => !!(u && (u.isGuest === true || u.guestLinkToken));
